import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Text, Stack, Select, TextInput, Button, Group, Alert, Box } from '@mantine/core'
import { IconAlertTriangle, IconScan, IconX } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { NotFoundException } from '@zxing/library'
import { getAssets, getAsset, scanLookupAsset, swapWithSpare } from '../../api/client'

interface Props {
  opened: boolean
  onClose: () => void
  jobId: number | null              // repair job id — swapWithSpare ต้องมี job ที่สร้างไว้แล้วเท่านั้น
  originalAssetId?: number | null   // ใช้ดึง category สดจาก asset จริง — originalCategory เพียงอย่าง
  // เดียวไม่พอ เพราะฝั่งหน้าเรียก (MaintenancePage/ScanPage) มักคำนวณมาจาก list ที่กรองสถานะ
  // AVAILABLE ไว้ก่อนแล้ว ซึ่งไม่มีอุปกรณ์ตัวนี้อยู่ในนั้นเลย (มันกำลังซ่อมอยู่ ไม่ใช่ AVAILABLE)
  // ทำให้หาไม่เจอและได้ null เสมอ — ดึงสดด้วย id ตรงๆ แทน กันปัญหานี้ทั้งหมด
  originalAssetCode?: string | null
  originalSerialNumber?: string | null
  originalCategory?: string | null
  originalLocation?: string | null
  onSuccess: () => void
}

/**
 * SwapWithSpareModal — ใช้ร่วมกันทั้งเว็บ (หน้า Maintenance) และมือถือ (Quick Scan)
 * เรียก swapWithSpare() ตัวที่ถูกต้องเสมอ (ของเดิมคง IN REPAIR + location เดิม,
 * ย้าย location/owner ให้ตัวสำรองแทน) — ไม่ใช่ swapAssets() ทั่วไปที่ใช้กับปุ่ม
 * "Swap Location & Owner" แยกต่างหาก
 */
export default function SwapWithSpareModal({
  opened, onClose, jobId, originalAssetId, originalAssetCode, originalSerialNumber, originalCategory, originalLocation, onSuccess,
}: Props) {
  const { t } = useTranslation()
  const [availableAssets, setAvailableAssets] = useState<any[]>([])
  const [loadingAssets, setLoadingAssets] = useState(false)
  const [spareAssetId, setSpareAssetId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [swapping, setSwapping] = useState(false)
  // category ที่ดึงสดจาก originalAssetId — ใช้แทน originalCategory prop เมื่อดึงสำเร็จ (แม่นกว่า
  // เพราะไม่ผ่าน list ที่กรองสถานะไว้ก่อนแล้วแบบ prop เดิม) fallback กลับไปใช้ originalCategory
  // prop ถ้าดึงไม่สำเร็จหรือไม่ได้ส่ง originalAssetId มา
  const [resolvedCategory, setResolvedCategory] = useState<string | null | undefined>(originalCategory)

  const [scanning, setScanning] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const controlsRef = useRef<any>(null)
  const decodedRef = useRef(false)

  useEffect(() => {
    if (!opened) return
    setSpareAssetId(null); setNote(''); setScanning(false); setScanError(null)
    setLoadingAssets(true)
    getAssets('AVAILABLE').then(r => setAvailableAssets(r.data ?? [])).catch(() => {}).finally(() => setLoadingAssets(false))
    setResolvedCategory(originalCategory)
    if (originalAssetId) {
      getAsset(originalAssetId).then(r => { if (r.data?.category) setResolvedCategory(r.data.category) }).catch(() => {})
    }
  }, [opened, originalAssetId])

  const stopScan = () => {
    try {
      if (controlsRef.current) { controlsRef.current.stop(); controlsRef.current = null }
      BrowserMultiFormatReader.releaseAllStreams()
      if (videoRef.current) videoRef.current.srcObject = null
    } catch {}
    setScanning(false)
  }

  const handleScanResult = async (code: string) => {
    stopScan()
    try {
      const res = await scanLookupAsset(code.trim())
      const found = res.data?.found ? res.data : null
      if (!found) { setScanError(t('swapSpare.scanNotFound', 'No asset matches this code')); return }
      if (found.status !== 'AVAILABLE') {
        setScanError(t('swapSpare.scanNotAvailable', 'This asset is not AVAILABLE')); return
      }
      if (resolvedCategory && found.category !== resolvedCategory) {
        setScanError(t('swapSpare.scanWrongCategory', 'Category does not match the original asset')); return
      }
      setSpareAssetId(String(found.id))
      setScanError(null)
      // เผื่อยังไม่อยู่ใน list ที่โหลดไว้ (เพิ่งสแกนเจอ) เพิ่มเข้าไปให้เลือกได้
      setAvailableAssets(prev => prev.some(a => a.id === found.id) ? prev : [...prev, found])
    } catch {
      setScanError(t('swapSpare.scanNotFound', 'No asset matches this code'))
    }
  }

  const startScan = async () => {
    try {
      decodedRef.current = false
      setScanError(null)
      setScanning(true)
      const codeReader = new BrowserMultiFormatReader()
      if (!videoRef.current) return
      controlsRef.current = await codeReader.decodeFromConstraints(
        { video: { facingMode: 'environment' } },
        videoRef.current,
        (result, err) => {
          if (result && !decodedRef.current) { decodedRef.current = true; handleScanResult(result.getText()) }
          if (err && !(err instanceof NotFoundException)) { /* ignore continuous no-result */ }
        }
      )
    } catch {
      notifications.show({ color: 'red', message: t('scan.cameraError', 'Could not access camera') })
      setScanning(false)
    }
  }

  useEffect(() => () => stopScan(), [])

  const filteredAssets = availableAssets.filter(a =>
    (!resolvedCategory || a.category === resolvedCategory) && a.assetCode !== originalAssetCode
  )
  const spare = filteredAssets.find(a => String(a.id) === spareAssetId)

  const handleConfirm = async () => {
    if (!jobId || !spareAssetId) return
    setSwapping(true)
    try {
      await swapWithSpare(jobId, { spareAssetId: Number(spareAssetId), note: note || undefined })
      notifications.show({ color: 'violet', message: t('swapSpare.success', 'Swap successful — spare is now in place') })
      onSuccess()
      onClose()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('swapSpare.failed', 'Swap failed') })
    } finally { setSwapping(false) }
  }

  return (
    <Modal opened={opened} onClose={() => { stopScan(); onClose() }} size="md" radius="lg"
      title={<Text fw={700} style={{ fontFamily: 'Inter,sans-serif' }}>🔄 {t('swapSpare.title', 'Replacement')}</Text>}>
      <Stack gap="sm">
        {/* คำอธิบายฟังก์ชัน — ต่างจาก Swap Asset ตรงที่ (1) ใช้ได้เฉพาะอุปกรณ์สถานะ AVAILABLE
        เท่านั้น (Swap Asset ใช้ได้ไม่ว่าสถานะอะไร) และ (2) ทิศทางเดียว: ของเดิมยังคง IN REPAIR
        ที่ location เดิม ตัวสำรองย้ายมาแทนที่ — ไม่ใช่แลกที่กันแบบ Swap Asset ทั่วไป
        เหมือนกันตรงที่สลับ location + owner + name ครบเหมือนกัน และใช้ได้แค่ประเภทเดียวกันเท่านั้น */}
        <Box p="xs" style={{ background: '#f5f3ff', borderRadius: 8, border: '1px solid #ddd6fe' }}>
          <Text size="xs" c="violet.8">
            ℹ️ {t('swapSpare.explainer', 'Use this when a repaired asset is still out and a spare needs to cover its location, owner, and name in the meantime. Unlike Swap Asset, the spare must be AVAILABLE, and only the spare moves — the original asset stays IN REPAIR at its original location. Only works between assets of the same category.')}
          </Text>
        </Box>

        {originalAssetCode && (
          <Alert icon={<IconAlertTriangle size={14} />} color="blue" variant="light">
            <Text size="sm">
              <b>{originalAssetCode}</b> {originalSerialNumber ? `(S/N: ${originalSerialNumber})` : ''} {t('swapSpare.staysInRepair', 'will remain IN REPAIR at the original location.')}<br />
              {t('swapSpare.spareMoves', 'The chosen spare will move to replace it there.')}
            </Text>
          </Alert>
        )}

        {!scanning ? (
          <>
            <Group gap="sm" align="flex-end">
              <Select
                label={t('swapSpare.spareLabel', 'Select Spare Asset')}
                placeholder={t('swapSpare.sparePlaceholder', 'Search by Asset Code or name...')}
                searchable clearable
                data={filteredAssets.map(a => ({
                  value: String(a.id),
                  label: `${a.assetCode} — ${a.name} (S/N: ${a.serialNumber ?? '-'}) · ${a.location ?? 'No location'}`,
                }))}
                value={spareAssetId}
                onChange={setSpareAssetId}
                disabled={loadingAssets}
                style={{ flex: 1 }}
              />
              <Button variant="light" leftSection={<IconScan size={16} />} onClick={startScan}>
                {t('swapSpare.scanBtn', 'Scan')}
              </Button>
            </Group>
            <Text size="xs" c="dimmed">
              {t('swapSpare.filterHint', 'Only AVAILABLE assets in the same category')}{resolvedCategory ? ` (${resolvedCategory})` : ''}
            </Text>
          </>
        ) : (
          <Stack gap="xs">
            <Box style={{ position: 'relative', width: '100%', aspectRatio: '1', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
              <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
              <Box style={{ position: 'absolute', inset: '18%', border: '2px solid #a78bfa', borderRadius: 12, pointerEvents: 'none' }} />
            </Box>
            <Button variant="light" color="red" leftSection={<IconX size={14} />} onClick={stopScan}>
              {t('scan.stopScanning', 'Stop Scanning')}
            </Button>
          </Stack>
        )}

        {scanError && <Text size="xs" c="red">{scanError}</Text>}

        {spare && (
          <Box style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px' }}>
            <Text size="xs" fw={600} c="teal" mb={4}>{t('swapSpare.selectedSpare', 'Selected spare:')}</Text>
            <Text size="sm" fw={600}>{spare.assetCode} — {spare.name}</Text>
            <Text size="xs" c="dimmed">S/N: {spare.serialNumber ?? '-'} · {t('common.location', 'Location')}: {spare.location ?? '-'}</Text>
          </Box>
        )}

        <TextInput label={t('repair.noteOptional', 'Note (optional)')} value={note} onChange={e => setNote(e.target.value)} />

        <Box style={{ background: '#fafafa', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
          <Text size="xs" fw={600} mb={6}>{t('swapSpare.willHappen', 'What will happen:')}</Text>
          <Text size="xs" c="dimmed">• <b>{originalAssetCode}</b> {originalSerialNumber ? `(S/N: ${originalSerialNumber})` : ''} → {t('swapSpare.stayInRepairAt', 'stays IN REPAIR, location unchanged')} ({originalLocation ?? '-'})</Text>
          <Text size="xs" c="dimmed">• {t('swapSpare.spareMovesTo', 'Spare selected → moves to')} {originalLocation ?? '-'}</Text>
          <Text size="xs" c="dimmed">• {t('swapSpare.nameSwapNote', 'Both assets also swap search names, so searching for either one still finds the item now sitting in its place.')}</Text>
        </Box>

        <Group justify="flex-end">
          <Button variant="default" onClick={() => { stopScan(); onClose() }}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="violet" loading={swapping} disabled={!spareAssetId || swapping} onClick={handleConfirm}>
            {t('swapSpare.confirm', 'Confirm Swap')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
